import type {
  CreateProjectInput,
  ListProjectsQuery,
  ProjectDto,
  CoverageRefreshDto,
  ProjectCoverageDto,
  ProjectOverviewDto,
  UpdateProjectInput,
} from '@tms/shared';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

export type ProjectListParams = Partial<Pick<ListProjectsQuery, 'status' | 'q'>> & {
  page?: number;
  perPage?: number;
};

export const projectsQueryOptions = (params: ProjectListParams) =>
  queryOptions({
    queryKey: queryKeys.projects.list(params),
    queryFn: ({ signal }) => api.list<ProjectDto>('/projects', { ...params }, signal),
    placeholderData: (previous) => previous,
  });

export const projectQueryOptions = (code: string) =>
  queryOptions({
    queryKey: queryKeys.projects.detail(code),
    queryFn: ({ signal }) => api.get<ProjectDto>(`/projects/${code}`, undefined, signal),
  });

export const projectOverviewQueryOptions = (code: string) =>
  queryOptions({
    queryKey: queryKeys.projects.overview(code),
    queryFn: ({ signal }) =>
      api.get<ProjectOverviewDto>(`/projects/${code}/overview`, undefined, signal),
  });

export const projectCoverageQueryOptions = (code: string, days: number) =>
  queryOptions({
    queryKey: queryKeys.projects.coverage(code, days),
    queryFn: ({ signal }) =>
      api.get<ProjectCoverageDto>(`/projects/${code}/coverage`, { days }, signal),
  });

export const useProjects = (params: ProjectListParams) => useQuery(projectsQueryOptions(params));
export const useProject = (code: string) => useQuery(projectQueryOptions(code));
export const useProjectOverview = (code: string) => useQuery(projectOverviewQueryOptions(code));
export const useProjectCoverage = (code: string, days: number) =>
  useQuery(projectCoverageQueryOptions(code, days));

/** Invalidates everything that shows project data after a mutation. */
const useProjectInvalidation = () => {
  const queryClient = useQueryClient();
  return (code?: string) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    if (code) void queryClient.invalidateQueries({ queryKey: queryKeys.projects.detail(code) });
  };
};

export const useCreateProject = () => {
  const invalidate = useProjectInvalidation();
  return useMutation({
    mutationFn: (input: CreateProjectInput) => api.post<ProjectDto>('/projects', input),
    onSuccess: (project) => invalidate(project.code),
  });
};

export const useUpdateProject = (code: string) => {
  const invalidate = useProjectInvalidation();
  return useMutation({
    mutationFn: (input: UpdateProjectInput) => api.patch<ProjectDto>(`/projects/${code}`, input),
    onSuccess: () => invalidate(code),
  });
};

export const useSetProjectStatus = (code: string) => {
  const invalidate = useProjectInvalidation();
  return useMutation({
    mutationFn: (action: 'archive' | 'restore') =>
      api.post<ProjectDto>(`/projects/${code}/${action}`),
    onSuccess: () => invalidate(code),
  });
};

export const useDeleteProject = () => {
  const invalidate = useProjectInvalidation();
  return useMutation({
    mutationFn: (code: string) => api.delete(`/projects/${code}`),
    onSuccess: () => invalidate(),
  });
};

/** «Refresh now»: stores the linked projects' finished pipelines, then the card reloads. */
export const useRefreshCoverage = (code: string) => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<CoverageRefreshDto>(`/projects/${code}/coverage/refresh`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['projects', 'coverage', code] }),
  });
};
